import React, { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Plus, Trash2 } from "lucide-react";
import { getApiUrl } from "../../config/api";
import "../../styles/DashboardLayout.css";
import "../../styles/UtopiaPages.css";

interface Notice {
  id: string;
  title: string;
  message: string;
  audience: string;
  createdAt: string;
}

const HeadteacherNotices: React.FC = () => {
  const [notices, setNotices] = useState<Notice[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ title: "", message: "", audience: "all" });
  const [message, setMessage] = useState<string | null>(null);

  const token = localStorage.getItem("token");

  const fetchNotices = useCallback(async () => {
    try {
      const res = await axios.get(getApiUrl("communications/notices"), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotices(res.data.notices || []);
    } catch (error) {
      console.error("Error fetching notices:", error);
    }
  }, [token]);

  useEffect(() => {
    fetchNotices();
  }, [fetchNotices]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await axios.post(getApiUrl("communications/notices"), formData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage("Notice published successfully.");
      setFormData({ title: "", message: "", audience: "all" });
      setIsModalOpen(false);
      fetchNotices();
    } catch (error: any) {
      setMessage(error.response?.data?.message || "Failed to publish notice.");
    }
  };

  const handleDelete = async (id: string) => {
    if (!window.confirm("Delete this notice?")) return;
    try {
      await axios.delete(getApiUrl(`communications/notices/${id}`), {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessage("Notice deleted.");
      fetchNotices();
    } catch (error: any) {
      setMessage(error.response?.data?.message || "Failed to delete notice.");
    }
  };

  return (
    <div className="dashboard-container">
      <main className="dashboard-main">
        <div className="utopia-page-container">
          <div className="utopia-table-header">
            <div>
              <h2>School Notices</h2>
              <p className="page-subtitle">Publish announcements for staff and learners.</p>
            </div>
            <button className="utopia-btn-primary flex items-center gap-2" onClick={() => setIsModalOpen(true)}>
              <Plus size={18} /> New Notice
            </button>
          </div>

          {message && (
            <div className="p-4 mb-4 rounded-xl bg-slate-50 text-slate-700 text-sm">
              {message}
            </div>
          )}

          <div className="space-y-4">
            {notices.map((notice) => (
              <div key={notice.id} className="bg-white p-6 rounded-2xl border border-slate-100 shadow-sm">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="text-lg font-bold text-slate-900">{notice.title}</h3>
                    <p className="text-sm text-slate-500 mt-1">{new Date(notice.createdAt).toLocaleString()}</p>
                    <p className="text-sm text-slate-700 mt-3 whitespace-pre-line">{notice.message}</p>
                    <span className="inline-flex mt-4 text-xs px-2 py-1 rounded-full bg-teal-50 text-teal-700">
                      Audience: {notice.audience}
                    </span>
                  </div>
                  <button className="utopia-btn-icon text-red-400 hover:bg-red-50" onClick={() => handleDelete(notice.id)}>
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
            {notices.length === 0 && (
              <div className="text-center text-slate-500 py-12">No notices published yet.</div>
            )}
          </div>
        </div>
      </main>

      {isModalOpen && (
        <div className="utopia-modal-overlay" onClick={(e) => e.target === e.currentTarget && setIsModalOpen(false)}>
          <div className="utopia-modal-content max-w-lg" onClick={(e) => e.stopPropagation()}>
            <div className="utopia-modal-header">
              <h3 className="text-lg font-bold">New Notice</h3>
              <button className="utopia-modal-close-btn" onClick={() => setIsModalOpen(false)}>×</button>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="utopia-form-group">
                <label className="utopia-label required">Title</label>
                <input
                  className="utopia-input"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  required
                />
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label required">Message</label>
                <textarea
                  className="utopia-input"
                  rows={4}
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  required
                />
              </div>
              <div className="utopia-form-group">
                <label className="utopia-label">Audience</label>
                <select
                  className="utopia-input"
                  value={formData.audience}
                  onChange={(e) => setFormData({ ...formData, audience: e.target.value })}
                >
                  <option value="all">All</option>
                  <option value="staff">Staff</option>
                  <option value="learners">Learners</option>
                </select>
              </div>
              <div className="flex gap-3 mt-4">
                <button type="button" className="utopia-btn-secondary flex-1" onClick={() => setIsModalOpen(false)}>
                  Cancel
                </button>
                <button type="submit" className="utopia-btn-primary flex-1">Publish</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default HeadteacherNotices;









